/**
 * Population & sample selection (Population C/A tab): the figures of the population / IPE tie-out, its tolerances,
 * the generated conclusion paragraph and the period covered. The tab and the working paper (PDF / Excel) are both
 * built from these functions.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { formatMoney } from '../../../shared/format/money.ts';
import { completeYear } from '../../../shared/format/period.ts';

/** The run's currency: the one marked most in its population / evidence files, as found by the AI service. */
export function runCurrency(run: any): string | null {
  const population = run?.population || {};
  return population.parse_metadata?.currency
    || population.ipe_ai_comparison?.currency
    || run?.execution?.executive_summary?.currency
    || null;
}

export const fmtRange = (r?: { from?: string | null; to?: string | null } | null) =>
  (r?.from && r?.to ? (r.from === r.to ? r.from : `${r.from} – ${r.to}`) : '—');

export interface PopulationPeriod {
  start: string | null;
  end: string | null;
  population: { from: string; to: string } | null;
  proof: { from: string; to: string } | null;
  result: 'within' | 'outside' | 'unknown';
  rangesMatch: boolean | null;
  sentence: string;
}

/** The dates the population and the IPE proof cover, against the testing period (the reviewer's dates if set). */
export function populationPeriod(run: any): PopulationPeriod {
  const population = run?.population || {};
  const day = (d: any) => (d ? String(d).slice(0, 10) : null);
  const start = day(population.human_testing_period_start || population.testing_period_start || run?.testing_period_start);
  const end = day(population.human_testing_period_end || population.testing_period_end || run?.testing_period_end);
  const meta = population.parse_metadata || {};
  const ai = population.ipe_ai_comparison || {};
  const pick = (r: any) => completeYear(r, start, end);
  const pop = pick(meta.population_date_range) || pick(ai.population_file?.date_range);
  const proof = pick(meta.ipe_date_range) || pick(ai.ipe_proof?.date_range);
  const ranges = [pop, proof].filter(Boolean) as Array<{ from: string; to: string }>;
  let result: PopulationPeriod['result'] = 'unknown';
  if (ranges.length && start && end) {
    result = ranges.every((r) => r.from >= start && r.to <= end) ? 'within' : 'outside';
  }
  const rangesMatch = pop && proof ? pop.from === proof.from && pop.to === proof.to : null;
  let sentence = '';
  if (pop && start && end) {
    sentence = result === 'within'
      ? `The population covers ${fmtRange(pop)}, within the testing period ${start} to ${end}.`
      : result === 'outside'
        ? `The population covers ${fmtRange(pop)}, which falls outside the testing period ${start} to ${end}; the difference must be explained.`
        : '';
  }
  return { start, end, population: pop, proof, result, rangesMatch, sentence };
}

const SELECTION_LABEL: Record<string, string> = {
  statistical: 'Statistical random', haphazard: 'Haphazard (non-statistical)',
  judgmental: 'Judgmental', random: 'Random', file_upload: 'File upload', manual_count: 'Manual count',
};

const num = (v: any) => (v != null ? Number(v) : null);

/** The population figures as the tab reads them. */
export function populationFacts(run: any) {
  const population = run?.population || {};
  const notRequired = run?.population_status === 'not_required';
  const samplingMethod = run?.sampling_method || population.source_type || null;
  const selectionLabel = (samplingMethod && SELECTION_LABEL[samplingMethod]) || samplingMethod || 'Not specified';
  const ipeStatus: string | null = population.ipe_status || null;
  const filteredTotal = num(population.filtered_total_amount);
  const popRecordCount = num(population.ipe_population_record_count);
  const proofFileName: string | null = population.ipe_proof_file_name || null;
  const hasIPEData = !!(ipeStatus && ipeStatus !== 'pending' && (filteredTotal != null || popRecordCount != null || proofFileName));
  return {
    notRequired,
    totalPopulation: run?.total_population ?? population.total_count ?? null,
    samplingMethod,
    selectionLabel,
    selectionText: notRequired ? 'Evidence files' : selectionLabel,
    identifierColumn: population.identifier_column || null,
    ipeStatus,
    // "tied_out" (manual tie-out) and "verified" (AI comparison) both mean the population passed completeness.
    isVerified: ipeStatus === 'verified' || ipeStatus === 'tied_out',
    isException: ipeStatus === 'exception',
    filteredTotal,
    independentTotal: num(population.independent_total_amount),
    ipeDelta: num(population.ipe_delta),
    popRecordCount,
    proofRecordCount: num(population.ipe_proof_record_count),
    popColumnCount: num(population.ipe_population_column_count),
    proofColumnCount: num(population.ipe_proof_column_count),
    popFileName: (population.population_file_name || null) as string | null,
    proofFileName,
    ipeVerifiedAt: population.ipe_verified_at || null,
    ipeSourceNote: (population.ipe_source_note || null) as string | null,
    hasIPEData,
    legacyIPEValidated: !hasIPEData && !!run?.ipe_validated,
  };
}

export type PopulationFacts = ReturnType<typeof populationFacts>;

/** Tolerances of the tie-out table: count ±0.1% (at least 1 record), amount ±0.1% of the population total. */
export function tieOutTolerances(f: PopulationFacts) {
  const countTolerance = f.popRecordCount != null ? Math.max(1, Math.ceil(f.popRecordCount * 0.001)) : null;
  const countDelta = f.popRecordCount != null && f.proofRecordCount != null ? Math.abs(f.popRecordCount - f.proofRecordCount) : null;
  const amtTolerance = f.filteredTotal != null ? Math.abs(f.filteredTotal * 0.001) : null;
  const amtDelta = f.filteredTotal != null && f.independentTotal != null ? Math.abs(f.filteredTotal - f.independentTotal) : null;
  return {
    countTolerance, countDelta,
    countMatch: countTolerance != null && countDelta != null ? countDelta <= countTolerance : null,
    amtTolerance, amtDelta,
    amtMatch: amtTolerance != null && amtDelta != null ? amtDelta <= amtTolerance : null,
  };
}

const fmtCount = (v: number | null) => (v != null ? Number(v).toLocaleString() : '—');
const plural = (n: number, word: string) => `${word}${n !== 1 ? 's' : ''}`;

/** The generated conclusion paragraph of the tie-out (null when there is no tie-out). The reviewer's own text, when
 *  saved (population.human_ipe_conclusion), is shown instead. */
export function ipeConclusionText(f: PopulationFacts, period: PopulationPeriod, currency: string | null): string | null {
  if (!f.hasIPEData) return null;
  const fmtAmt = (v: number | null) => formatMoney(v, currency, { maxDecimals: 2 });
  const fmtDelta = (v: number | null) => formatMoney(v, currency, { maxDecimals: 2, sign: true });
  const verifiedDate = f.ipeVerifiedAt
    ? new Date(f.ipeVerifiedAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;
  const popLabel = f.popFileName ? `"${f.popFileName}"` : 'the population extract';
  const proofLabel = f.proofFileName ? `"${f.proofFileName}"` : 'the independent proof file';

  let countSentence = '';
  if (f.popRecordCount != null && f.proofRecordCount != null) {
    const countVar = Math.abs(f.popRecordCount - f.proofRecordCount);
    countSentence = `The population extract contained ${fmtCount(f.popRecordCount)} ${plural(f.popRecordCount, 'record')} and the independent source contained ${fmtCount(f.proofRecordCount)} ${plural(f.proofRecordCount, 'record')}; record-count variance: ${countVar === 0 ? '0 (agree)' : `${fmtCount(countVar)} ${plural(countVar, 'record')} (exception)`}.`;
  } else if (f.popRecordCount != null) {
    countSentence = `The population extract contained ${fmtCount(f.popRecordCount)} ${plural(f.popRecordCount, 'record')}.`;
  }

  let amtSentence = '';
  if (f.filteredTotal != null && f.independentTotal != null) {
    const delta = Math.abs(f.filteredTotal - f.independentTotal);
    const base = Math.max(Math.abs(f.independentTotal), 1);
    const pct = ((delta / base) * 100).toFixed(2);
    const withinTol = delta <= base * 0.001;
    const deltaStr = delta < 0.01 ? fmtAmt(0) : fmtDelta(f.ipeDelta);
    amtSentence = `Total amount per population extract: ${fmtAmt(f.filteredTotal)}; per independent source: ${fmtAmt(f.independentTotal)}. Amount variance: ${deltaStr} (${pct}%${withinTol ? ' — within 0.1% SOX tolerance' : ' — exceeds 0.1% SOX tolerance threshold'}).`;
  } else if (f.filteredTotal != null) {
    amtSentence = `Total amount per population extract: ${fmtAmt(f.filteredTotal)}.`;
  }
  const source = f.ipeSourceNote ? ` (${f.ipeSourceNote})` : '';

  if (f.isVerified) {
    return [
      `Population completeness verified${verifiedDate ? ` on ${verifiedDate}` : ''}.`,
      `The auditor obtained the client-provided population extract ${popLabel} and agreed it to the independently obtained proof file ${proofLabel}${source} using an AI-assisted record-count and total-amount comparison.`,
      countSentence,
      amtSentence,
      period.sentence,
      period.result === 'outside'
        ? 'Records and amounts agree, but the dates covered fall outside the testing period: the difference must be explained before the population can be relied on.'
        : 'Based on the above, the population extract is considered complete and the auditor is satisfied that it represents the full population for the testing period. No differences noted.',
    ].filter(Boolean).join(' ');
  }
  if (f.isException) {
    return [
      `Population completeness exception noted${verifiedDate ? ` on ${verifiedDate}` : ''}.`,
      `The auditor compared the client-provided population extract ${popLabel} to the independently obtained proof file ${proofLabel}${source}.`,
      countSentence,
      amtSentence,
      period.sentence,
      'The variance exceeds acceptable thresholds. The auditor must document an explanation for the difference before this test run can be signed off.',
    ].filter(Boolean).join(' ');
  }
  return null;
}

const stripQuotes = (r: any) => (typeof r === 'string' ? r.replace(/^["']+|["']+$/g, '').trim() : r);
const refKey = (r: any) => String(stripQuotes(r) ?? '').toLowerCase().replace(/\s+/g, ' ');

/** The sample list under the tie-out: the run's saved selection (else the evidence map's samples), each sample's
 *  extracted values and its evidence file. */
export function populationSamples(run: any, evidenceMap: any[], attributes: any[]) {
  const runSamples = (run?.samples || []).map((s: any) => stripQuotes(s.record_ref || s));
  const refs: string[] = runSamples.length > 0
    ? [...new Set<string>(runSamples)]
    : [...new Set<string>(evidenceMap.map((c) => stripQuotes(c.sample_ref)).filter(Boolean))];

  const fieldData: Record<string, Record<string, any>> = {};
  attributes.forEach((attr) => (attr.sample_results || []).forEach((sr: any) => {
    const key = refKey(sr.record_ref);
    if (!fieldData[key]) fieldData[key] = sr.attributes || {};
  }));

  const sampleToFile: Record<string, string> = {};
  evidenceMap.forEach((c) => {
    const ref = refKey(c.sample_ref || c.record_ref);
    if (ref && !sampleToFile[ref]) sampleToFile[ref] = c.file_name || '';
  });
  attributes.forEach((attr) => (attr.sample_results || []).forEach((sr: any) => {
    const ref = refKey(sr.record_ref);
    if (sampleToFile[ref]) return;
    const fn = sr.citation?.source_file || '';
    if (ref && fn) sampleToFile[ref] = fn;
  }));
  return { refs, fieldData, sampleToFile, key: refKey };
}

export type TieOutResult = 'match' | 'exception' | 'within' | 'outside' | 'none';

export interface TieOutRow {
  id: 'records' | 'columns' | 'period' | 'amount';
  metric: string;
  note: string | null;      // under the metric (the tolerance rule, the testing period)
  population: string;
  proof: string;
  diff: string;
  tolerance: string;
  result: TieOutResult;
  resultText: string;       // '✓ Match', '✗ Exception', '✓ Within period', ...
}

const RESULT_TEXT: Record<TieOutResult, string> = {
  match: '✓ Match', exception: '✗ Exception', within: '✓ Within period', outside: '✗ Outside period', none: '—',
};

const matchResult = (ok: boolean | null): TieOutResult => (ok === true ? 'match' : ok === false ? 'exception' : 'none');

/** The rows of the completeness & accuracy table, as shown on the Population tab. */
export function tieOutRows(run: any, f: PopulationFacts, period: PopulationPeriod, currency: string | null): TieOutRow[] {
  const t = tieOutTolerances(f);
  const fmtAmt = (v: number | null) => formatMoney(v, currency, { maxDecimals: 2 });
  const count = (v: number | null) => (v != null ? Number(v).toLocaleString() : '—');
  const rows: TieOutRow[] = [];
  if (f.popRecordCount != null || f.proofRecordCount != null) {
    const r = matchResult(t.countMatch);
    rows.push({
      id: 'records', metric: 'Record count',
      note: t.countTolerance != null ? `0.1% tolerance = ${t.countTolerance.toLocaleString()} ${plural(t.countTolerance, 'record')}` : null,
      population: count(f.popRecordCount), proof: count(f.proofRecordCount),
      diff: t.countDelta?.toLocaleString() ?? '—', tolerance: t.countTolerance?.toLocaleString() ?? '—',
      result: r, resultText: RESULT_TEXT[r],
    });
  }
  if (f.popColumnCount != null || f.proofColumnCount != null) {
    const both = f.popColumnCount != null && f.proofColumnCount != null;
    const r = both ? (f.popColumnCount === f.proofColumnCount ? 'match' : 'exception') : 'none';
    rows.push({
      id: 'columns', metric: 'Column count', note: null,
      population: count(f.popColumnCount), proof: count(f.proofColumnCount),
      diff: both ? Math.abs((f.popColumnCount as number) - (f.proofColumnCount as number)).toLocaleString() : '—',
      tolerance: '—', result: r, resultText: RESULT_TEXT[r],
    });
  }
  const population = run?.population || {};
  const pr: TieOutResult = period.result === 'within' ? 'within' : period.result === 'outside' ? 'outside' : 'none';
  rows.push({
    id: 'period', metric: 'Period covered',
    note: period.start && period.end ? `Testing period ${period.start} – ${period.end}` : null,
    population: fmtRange(period.population), proof: fmtRange(period.proof),
    diff: period.rangesMatch === true ? 'Same' : period.rangesMatch === false ? 'Differ' : '—',
    tolerance: 'Within testing period', result: pr,
    resultText: pr !== 'none' ? RESULT_TEXT[pr]
      : (population.parse_metadata || population.ipe_ai_comparison ? 'Dates not found' : 'Not recorded for this run'),
  });
  if (f.filteredTotal != null || f.independentTotal != null) {
    const r = matchResult(t.amtMatch);
    rows.push({
      id: 'amount', metric: 'Total amount', note: null,
      population: fmtAmt(f.filteredTotal), proof: fmtAmt(f.independentTotal),
      diff: t.amtDelta != null ? fmtAmt(t.amtDelta) : '—', tolerance: t.amtTolerance != null ? fmtAmt(t.amtTolerance) : '—',
      result: r, resultText: RESULT_TEXT[r],
    });
  }
  return rows;
}
